import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { useTheme } from '../../../../../theming/useTheme';
import { LinkList } from './LinkList';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';
import { SubMenu } from './SubMenu';
import { useSubMenu, SubMenuReducerProps } from './subMenuReducer';
import { AnimationDirectionType } from '../menuAnimations.util';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';

export const componentConfigThemeKey = 'fixedNav';

interface FixedNavProps {
  rowHeight: number;
  navHeightLarge: number;
  ulRef: React.RefObject<HTMLUListElement>;
  links: NavigationLink[] | null;
  pagePath: string;
  activeStateColor: string;
  hoverStateColor: string;
  idleColor: string;
  linkFontSizeLarge: TypographySize;
  linkFontSizeMedium: TypographySize;
  linkFontSizeSmall: TypographySize;
  linkDropdownIconColor: string;
  menuFoldState: AnimationDirectionType;
  animationDuration: number;
}

interface FixedNavThemeProps {
  linkBackgroundColor: string;
  baseUnit: BaseUnit;
  subMenuLinksBackgroundColor: string;
}

const getStyles = (navHeightLarge: number, baseUnit: BaseUnit, linkBackgroundColor: string, rowHeight: number, subMenuState: SubMenuReducerProps) => {
  const backgroundWidth = subMenuState.element ? subMenuState.element.getBoundingClientRect().width : 0;
  return StyleSheet.create({
    fixedNav: {
      height: `${navHeightLarge}px`,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
    navHiddenLayer: {
      overflow: 'hidden',
      height: `${navHeightLarge}px`,
    },
    menuContainer: {
      position: 'relative',
    },
    fixedUl: {
      display: 'inline',
      marginLeft: `${baseUnit}px`,
      padding: 0,
    },
    menuWrapper: {
      position: 'relative',
    },
    linkBackground: {
      position: 'absolute',
      backgroundColor: linkBackgroundColor,
      height: `${rowHeight * baseUnit}px`,
      top: `-${(rowHeight * baseUnit) / 2}px`,
      left: `${subMenuState.elementOffset - baseUnit / 2}px`,
      width: `${backgroundWidth + baseUnit}px`,
    },
  });
};

const getLinkBackgroundOffset = (linkElement: HTMLElement) => {
  const offset = linkElement.getBoundingClientRect().left - linkElement.parentElement!.parentElement!.getBoundingClientRect().left;
  return offset;
};

export const FixedNav: React.FunctionComponent<FixedNavProps> = props => {
  const { baseUnit, linkBackgroundColor, subMenuLinksBackgroundColor } = useTheme<FixedNavThemeProps>(componentConfigThemeKey);
  const [subMenuState, dispatch] = useSubMenu();
  const { navHeightLarge, ulRef, links, pagePath, activeStateColor, hoverStateColor, idleColor, rowHeight, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall, linkDropdownIconColor, menuFoldState, animationDuration } = props;

  const linkColors = { activeStateColor, hoverStateColor, idleColor };
  const styles = getStyles(navHeightLarge, baseUnit, linkBackgroundColor, rowHeight, subMenuState);
  const isMenuOpen = subMenuState.index >= 0;

  const fontStyles = { linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall };

  const scrollHandler = () => {
    dispatch({
      data: {
        index: subMenuState.index,
        links: null,
        element: null,
        elementOffset: 0,
      },
    });
  };

  React.useEffect(() => {
    window.addEventListener('scroll', scrollHandler);

    return () => window.removeEventListener('scroll', scrollHandler);
  });

  const onMenuToggle = (element: HTMLElement, index: number) => {
    const menuLinks = links![index].children;
    dispatch({
      data: {
        index,
        links: menuLinks,
        element,
        elementOffset: getLinkBackgroundOffset(element),
      },
    });
  };

  return (
    <ErrorBoundary>
      <div className={css(styles.menuWrapper)}>
        {
          isMenuOpen && (
            <React.Fragment>
              <div className={css(styles.linkBackground)} />
              <SubMenu
                {...linkColors}
                backgroundColor={subMenuLinksBackgroundColor}
                pagePath={pagePath}
                links={subMenuState.links!}
                parentElement={subMenuState.element!}
                {...fontStyles}
                shouldOffsetLeft={false}
                linkFontSizeLarge={linkFontSizeLarge}
                linkFontSizeMedium={linkFontSizeMedium}
                linkFontSizeSmall={linkFontSizeSmall}
                menuFoldState={menuFoldState}
                animationDuration={animationDuration}
              />
            </React.Fragment>
          )
        }
      </div>
      <nav className={css(styles.fixedNav)}>
        <div className={css(styles.navHiddenLayer)}>
          <ul className={css(styles.fixedUl)} ref={ulRef}>
            {links && <LinkList links={links} pagePath={pagePath} {...linkColors} onMenuToggle={onMenuToggle} linkDropdownIconColor={linkDropdownIconColor} {...fontStyles} />}
          </ul>
        </div>
      </nav>
    </ErrorBoundary>
  );
};
